import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import configs from '../config';

const API = configs.BACKEND_URL;

const PERMISSION_COPY = [
  'Access your Propel profile information',
  'Use your saved workspace permissions in the extension',
  'Continue proposal and Browser Assist workflows from Upwork',
];

function buildReturnUrl(searchParams) {
  return `/oauth/authorize?${searchParams.toString()}`;
}

// Redirects must stay on this app's /oauth/authorized page (mirrors the server check)
// so a crafted link can't send the decline flow to another origin or a javascript: URI.
function isAllowedRedirectUri(redirectUri) {
  try {
    const parsed = new URL(redirectUri);
    return parsed.origin === window.location.origin
      && parsed.pathname.replace(/\/+$/, '') === '/oauth/authorized';
  } catch {
    return false;
  }
}

function clearStoredAuthState() {
  useAuthStore.setState({
    user: null,
    userPermissions: [],
    isAuthenticated: false,
  });
}

function Spinner() {
  return <span style={styles.spinner} aria-hidden="true" />;
}

export default function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking | ready | authorizing | switching | error
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);

  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const responseType = searchParams.get('response_type');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const state = searchParams.get('state');

  const isValid = useMemo(() => {
    if (!clientId || !redirectUri || !responseType || !codeChallenge || !codeChallengeMethod || !state) {
      return false;
    }

    return clientId === 'propel-chrome-extension'
      && responseType === 'code'
      && codeChallengeMethod === 'S256'
      && isAllowedRedirectUri(redirectUri);
  }, [clientId, codeChallenge, codeChallengeMethod, redirectUri, responseType, state]);

  useEffect(() => {
    if (!isValid) {
      setStatus('error');
      setError('Invalid authorization parameters.');
      return;
    }

    let cancelled = false;

    const inspectSession = async () => {
      try {
        const response = await fetch(`${API}/api/oauth/session`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.status === 401) {
          clearStoredAuthState();
          window.location.href = `/login?redirect=${encodeURIComponent(buildReturnUrl(searchParams))}`;
          return;
        }

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Could not inspect your Propel session.');
        }

        if (!cancelled) {
          setSession(data);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not inspect your Propel session.');
          setStatus('error');
        }
      }
    };

    inspectSession();

    return () => {
      cancelled = true;
    };
  }, [isValid, searchParams]);

  const handleAuthorize = async () => {
    setStatus('authorizing');
    setError('');

    try {
      const response = await fetch(`${API}/api/oauth/authorize`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          state,
        }),
      });

      if (response.status === 401) {
        clearStoredAuthState();
        window.location.href = `/login?redirect=${encodeURIComponent(buildReturnUrl(searchParams))}`;
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Authorization failed');
      }

      if (!data.redirectUrl) {
        throw new Error('No redirect URL received.');
      }

      window.location.href = data.redirectUrl;
    } catch (err) {
      setError(err.message || 'Authorization failed');
      setStatus('error');
    }
  };

  const handleDecline = () => {
    const params = new URLSearchParams({
      error: 'access_denied',
      state,
    });
    window.location.href = `${redirectUri}?${params.toString()}`;
  };

  const handleSwitchAccount = async () => {
    setStatus('switching');
    setError('');

    try {
      await fetch(`${API}/api/user/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best effort: local store still needs to be cleared before redirecting.
    }

    clearStoredAuthState();

    window.location.href = `/login?redirect=${encodeURIComponent(buildReturnUrl(searchParams))}`;
  };

  const accountLabel = session?.user?.name || session?.user?.email || 'Propel account';
  const accountEmail = session?.user?.email || '';
  const workspaceLabel = session?.tenant?.name || session?.tenantSlug || '';
  const isBusy = status === 'authorizing' || status === 'switching';

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoMark}>P</div>

        {status === 'checking' && (
          <>
            <div style={styles.illustrationShell}>
              <span style={{ ...styles.spinner, ...styles.spinnerDark }} aria-hidden="true" />
            </div>
            <h1 style={styles.title}>Checking your Propel session</h1>
            <p style={styles.text}>Looking for the web account that can authorize this extension.</p>
          </>
        )}

        {session && status !== 'checking' && status !== 'error' && (
          <>
            <div style={styles.identityRow}>
              <div>
                <h1 style={styles.title}>Propel for Chrome would like to connect to your Propel account</h1>
                <p style={styles.text}>Authorize the extension to use your existing Propel session on this browser.</p>
              </div>
            </div>

            <div style={styles.permissionPanel}>
              <p style={styles.permissionHeading}>YOUR ACCOUNT WILL BE USED TO:</p>
              {PERMISSION_COPY.map((item) => (
                <div key={item} style={styles.permissionItem}>
                  <span style={styles.checkMark}>✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAuthorize}
              disabled={isBusy}
              style={{
                ...styles.primaryButton,
                ...(isBusy ? styles.primaryButtonDisabled : {}),
              }}
            >
              {status === 'authorizing' ? <Spinner /> : 'Authorize'}
            </button>

            <button type="button" onClick={handleDecline} disabled={isBusy} style={styles.linkButton}>
              Decline
            </button>

            <div style={styles.accountFooter}>
              <p style={styles.accountMeta}>
                Logged in as <strong>{accountLabel}</strong>
              </p>
              {accountEmail && <p style={styles.accountEmail}>{accountEmail}</p>}
              {workspaceLabel && <p style={styles.accountWorkspace}>{workspaceLabel}</p>}
              <button type="button" onClick={handleSwitchAccount} disabled={isBusy} style={styles.switchButton}>
                {status === 'switching' ? 'Switching account...' : 'Switch account'}
              </button>
            </div>

            {status === 'authorizing' && (
              <p style={styles.processingText}>Finalizing your extension authorization...</p>
            )}

            {status === 'switching' && (
              <p style={styles.processingText}>Signing out of the current Propel account...</p>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div style={styles.illustrationShell}>
              <div style={styles.errorGlyph}>!</div>
            </div>
            <h1 style={styles.title}>Authorization failed</h1>
            <p style={{ ...styles.text, color: '#b91c1c' }}>{error}</p>
            <button type="button" onClick={() => window.location.reload()} style={styles.primaryButton}>
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    background: 'linear-gradient(180deg, #f6f3ec 0%, #f8f6f1 100%)',
    fontFamily: '"Segoe UI", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    color: '#201f1d',
  },
  card: {
    width: '100%',
    maxWidth: '680px',
    padding: '36px 36px 28px',
    borderRadius: '26px',
    background: '#fbf9f4',
    border: '1px solid rgba(38, 33, 28, 0.08)',
    boxShadow: '0 30px 80px rgba(68, 59, 46, 0.08)',
    textAlign: 'center',
  },
  logoMark: {
    width: '52px',
    height: '52px',
    margin: '0 auto 20px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#4F46E5',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '24px',
    letterSpacing: '0.04em',
  },
  illustrationShell: {
    minHeight: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px',
  },
  title: {
    margin: '0 auto 10px',
    maxWidth: '520px',
    fontSize: '36px',
    lineHeight: 1.1,
    fontWeight: 700,
    letterSpacing: '-0.03em',
  },
  text: {
    margin: '0 auto 22px',
    maxWidth: '520px',
    fontSize: '17px',
    lineHeight: 1.55,
    color: '#57534e',
  },
  identityRow: {
    marginBottom: '6px',
  },
  permissionPanel: {
    margin: '22px auto 24px',
    maxWidth: '520px',
    padding: '24px',
    borderRadius: '18px',
    background: '#ffffff',
    textAlign: 'left',
    border: '1px solid rgba(38, 33, 28, 0.08)',
  },
  permissionHeading: {
    margin: '0 0 18px',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.03em',
    color: '#3f3a34',
  },
  permissionItem: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    fontSize: '16px',
    lineHeight: 1.45,
    color: '#292524',
    marginBottom: '12px',
  },
  checkMark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ea580c',
    fontWeight: 700,
    minWidth: '20px',
  },
  primaryButton: {
    width: '100%',
    maxWidth: '520px',
    minHeight: '58px',
    margin: '0 auto',
    border: 'none',
    borderRadius: '16px',
    background: '#111111',
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.72,
    cursor: 'wait',
  },
  linkButton: {
    border: 'none',
    background: 'transparent',
    marginTop: '16px',
    padding: '8px 16px',
    color: '#403b36',
    fontSize: '18px',
    cursor: 'pointer',
  },
  accountFooter: {
    marginTop: '28px',
    textAlign: 'center',
  },
  accountMeta: {
    margin: 0,
    fontSize: '18px',
    color: '#403b36',
  },
  accountEmail: {
    margin: '8px 0 0',
    fontSize: '16px',
    color: '#57534e',
  },
  accountWorkspace: {
    margin: '8px 0 0',
    fontSize: '15px',
    color: '#78716c',
  },
  switchButton: {
    marginTop: '16px',
    border: 'none',
    background: 'transparent',
    color: '#1f2937',
    fontSize: '18px',
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    cursor: 'pointer',
  },
  processingText: {
    marginTop: '18px',
    fontSize: '15px',
    color: '#57534e',
  },
  errorGlyph: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#fee2e2',
    color: '#b91c1c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '22px',
  },
  spinner: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.35)',
    borderTopColor: '#ffffff',
    animation: 'oauth-spin 0.8s linear infinite',
    display: 'inline-block',
  },
  spinnerDark: {
    border: '2px solid rgba(79, 70, 229, 0.18)',
    borderTopColor: '#4F46E5',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('oauth-consent-spin-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'oauth-consent-spin-style';
  styleEl.textContent = `
    @keyframes oauth-spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleEl);
}
